import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import "./Devices.css";

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [phaseDetection, setPhaseDetection] = useState(true);
  const [transformerGrouping, setTransformerGrouping] = useState(true);
  const [showVoltages, setShowVoltages] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetch("/api/devices")
      .then((res) => res.json())
      .then((data) => {
        const now = new Date();
        const updated = data.map((d) => {
          const lastSeen = new Date(d.lastSeen);
          const diffHours = (now - lastSeen) / (1000 * 60 * 60);
          return {
            ...d,
            status: diffHours > 24 ? "offline" : "online",
          };
        });
        setDevices(updated);
      })
      .catch((err) => console.error("Failed to fetch devices", err));
  }, []);

  const handleCheckbox = (deviceId) => {
    setSelectedDevices((prev) =>
      prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleChange = (deviceId, field, value) => {
    setDevices((prev) =>
      prev.map((d) => (d.deviceId === deviceId ? { ...d, [field]: value } : d))
    );
  };

  const handleSave = () => {
    setEditMode(false);
    fetch("/api/devices", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(devices),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          console.log("✅ Devices saved");
        } else {
          console.error("❌ Save failed", data);
        }
      })
      .catch((err) => console.error("❌ Error saving", err));
  };

  const handleAdd = () => {
    const newId = `MG-${String(devices.length + 1).padStart(3, "0")}`;
    const newDevice = {
      deviceId: newId,
      meter: "",
      account: "",
      voltageL1: 0,
      voltageL2: 0,
      voltageLL: 0,
      imei: "",
      address: "",
      gpsLat: "",
      gpsLong: "",
      phase: "",
      battery: "",
      transformerId: "",
      kva: "",
      knownPhase: "",
      lastSeen: null,
    };
    setDevices([...devices, newDevice]);
  };

  const handleRemove = () => {
    if (selectedDevices.length === 0) return;
    if (!window.confirm("Remove selected device(s)?")) return;
    setDevices(devices.filter((d) => !selectedDevices.includes(d.deviceId)));
    setSelectedDevices([]);
  };

  const getVoltageStyle = (v) => {
    if (v === null || v === undefined) return {};
    if (v <= 0) return { color: "red" };
    if (v < 110 || v > 130) return { color: "orange" };
    return {};
  };

  const getBatteryStyle = (v) => {
    return v < 3.7 ? { color: "darkred" } : {};
  };

  const isOffline = (lastSeen) => {
    if (!lastSeen) return false;
    const diff = (Date.now() - new Date(lastSeen).getTime()) / 1000;
    return diff > 86400;
  };

  const paginatedDevices = devices.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(devices.length / rowsPerPage);

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f4f4f4" }}>
      <Sidebar />
      <div style={{ flex: 1, padding: "1rem 2rem 2rem", overflowX: "auto" }}>
        <h2>Devices</h2>

        <div style={{ marginBottom: "1rem" }}>
          <button className="button-green" onClick={handleAdd}>Add Device</button>
          <button className="button-blue" onClick={() => setEditMode(true)} disabled={selectedDevices.length === 0}>Edit Device</button>
          <button className="button-gray" onClick={handleSave} disabled={!editMode}>Save</button>
          <button className="button-red" onClick={handleRemove} disabled={selectedDevices.length === 0}>Remove Device</button>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label><input type="checkbox" checked={phaseDetection} onChange={() => setPhaseDetection(!phaseDetection)} /> Phase Detection</label>
          <label style={{ marginLeft: "1rem" }}><input type="checkbox" checked={transformerGrouping} onChange={() => setTransformerGrouping(!transformerGrouping)} /> Transformer Grouping</label>
          <label style={{ marginLeft: "1rem" }}><input type="checkbox" checked={showVoltages} onChange={() => setShowVoltages(!showVoltages)} /> Show Voltages</label>
        </div>

        <table>
          <thead>
            <tr>
              <th></th>
              <th>Device ID</th>
              <th>Meter</th>
              <th>Account</th>
              {showVoltages && (<><th>L1-N</th><th>L2-N</th><th>L1-L2</th></>)}
              <th>IMEI</th>
              <th>Address</th>
              <th>GPS</th>
              <th>Phase</th>
              <th>Battery</th>
              <th>Last Seen</th>
              {transformerGrouping && (<><th>Transformer</th><th>kVA</th></>)}
            </tr>
          </thead>
          <tbody>
            {paginatedDevices.map((d) => {
              const offline = isOffline(d.lastSeen);
              const rowStyle = offline ? { color: "blue" } : {};
              return (
                <tr key={d.deviceId} style={rowStyle}>
                  <td><input type="checkbox" checked={selectedDevices.includes(d.deviceId)} onChange={() => handleCheckbox(d.deviceId)} /></td>
                  <td>{d.deviceId}</td>
                  <td>{editMode && selectedDevices.includes(d.deviceId) ? (<input value={d.meter || ""} onChange={(e) => handleChange(d.deviceId, "meter", e.target.value)} />) : (d.meter || d.meterNumber || "—")}</td>
                  <td>{editMode && selectedDevices.includes(d.deviceId) ? (<input value={d.account || ""} onChange={(e) => handleChange(d.deviceId, "account", e.target.value)} />) : (d.account || d.accountNumber || "—")}</td>
                  {showVoltages && (
                    <>
                      <td style={getVoltageStyle(d.voltageL1)}>{d.voltageL1 ?? "—"}</td>
                      <td style={getVoltageStyle(d.voltageL2)}>{d.voltageL2 ?? "—"}</td>
                      <td style={getVoltageStyle(d.voltageLL)}>{d.voltageLL ?? "—"}</td>
                    </>
                  )}
                  <td>{d.imei || "—"}</td>
                  <td>{d.address || "—"}</td>
                  <td>{`${d.gpsLat ?? "—"}, ${d.gpsLong ?? "—"}`}</td>
                  <td>{phaseDetection ? "Auto" : d.phase || "—"}</td>
                  <td style={getBatteryStyle(d.battery)}>{d.battery ? `${d.battery}V` : "—"}</td>
                  <td>{d.lastSeen ? new Date(d.lastSeen).toLocaleString("en-US", { timeZone: "America/Chicago" }) : "—"}</td>
                  {transformerGrouping && (<><td>{d.transformerId || "—"}</td><td>{d.kva || "—"}</td></>)}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ marginTop: "1rem", display: "flex", alignItems: "center" }}>
          <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}>&lt;</button>
          <span style={{ margin: "0 1rem" }}>Page</span>
          <select value={currentPage} onChange={(e) => setCurrentPage(Number(e.target.value))}>
            {Array.from({ length: totalPages }, (_, i) => (
              <option key={i + 1} value={i + 1}>{i + 1}</option>
            ))}
          </select>
          <span style={{ margin: "0 1rem" }}>of {totalPages}</span>
          <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}>&gt;</button>
          <label style={{ marginLeft: "2rem" }}>Rows per page:
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{ marginLeft: "0.5rem" }}
            >
              {[5, 10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
      </div>
    </div>
  );
};

export default Devices;
